import * as React from 'react';
import PropTypes from 'prop-types';

import { cn } from '@/lib/utils';

const Card = React.forwardRef(({ className, variant = 'default', ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card"
    className={cn(
      // Base card styles
      'relative flex flex-col overflow-hidden transition-all duration-200',
      // Default variant - matches Figma design
      variant === 'default' && [
        'bg-gray-50 dark:bg-gray-800',
        'border border-gray-200 dark:border-gray-700',
        'rounded-2xl',
        'shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.2)]',
      ],
      // Container variant - for nested content
      variant === 'container' && [
        'bg-sky-50 dark:bg-sky-900/20',
        'border border-sky-200 dark:border-sky-800',
        'rounded-2xl',
        'shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.2)]',
      ],
      // Action variant - for interactive cards
      variant === 'action' && [
        'bg-white dark:bg-gray-900',
        'border border-gray-200 dark:border-gray-700',
        'rounded-2xl',
        'shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.2)]',
        'hover:shadow-[0px_4px_8px_0px_rgba(0,0,0,0.1)] dark:hover:shadow-[0px_4px_8px_0px_rgba(0,0,0,0.3)]',
        'hover:border-gray-300 dark:hover:border-gray-600',
        'cursor-pointer',
      ],
      className
    )}
    {...props}
  />
));
Card.displayName = 'Card';
Card.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'container', 'action']),
};

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-header"
    className={cn(
      'flex flex-col space-y-1.5 p-6',
      // Support for action in header
      'has-[data-slot=card-action]:flex-row has-[data-slot=card-action]:items-start has-[data-slot=card-action]:justify-between has-[data-slot=card-action]:space-y-0',
      className
    )}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';
CardHeader.propTypes = {
  className: PropTypes.string,
};

const CardTitle = React.forwardRef(({ className, size = 'default', ...props }, ref) => (
  <h3
    ref={ref}
    data-slot="card-title"
    className={cn(
      'font-roboto-condensed font-semibold text-gray-900 dark:text-gray-100',
      // Size variants matching Figma typography
      size === 'sm' && 'text-base leading-6', // 16px
      size === 'default' && 'text-lg leading-6', // 18px  
      size === 'lg' && 'text-xl leading-7', // 20px
      size === 'xl' && 'text-2xl leading-8', // 24px
      className
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';
CardTitle.propTypes = {
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'default', 'lg', 'xl']),
};

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    data-slot="card-description"
    className={cn(
      'font-roboto-condensed font-normal text-gray-600 dark:text-gray-400 text-sm leading-5',
      className
    )}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';
CardDescription.propTypes = {
  className: PropTypes.string,
};

const CardAction = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-action"
    className={cn('flex items-center space-x-2', className)}
    {...props}
  />
));
CardAction.displayName = 'CardAction';
CardAction.propTypes = {
  className: PropTypes.string,
};

const CardContent = React.forwardRef(({ className, spacing = 'default', ...props }, ref) => (
  <div 
    ref={ref} 
    data-slot="card-content" 
    className={cn(
      // Base content styles
      'flex-1',
      // Spacing variants matching Figma
      spacing === 'none' && 'p-0',
      spacing === 'sm' && 'p-3', // 12px
      spacing === 'default' && 'p-4', // 16px
      spacing === 'lg' && 'p-6', // 24px
      // Default horizontal padding when no spacing specified
      spacing === undefined && 'px-6',
      className
    )} 
    {...props} 
  />
));
CardContent.displayName = 'CardContent';
CardContent.propTypes = {
  className: PropTypes.string,
  spacing: PropTypes.oneOf(['none', 'sm', 'default', 'lg']),
};

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-footer"
    className={cn(
      'flex items-center justify-between p-6 pt-0',
      // Add top border when needed
      '[&:not(:first-child)]:border-t [&:not(:first-child)]:border-gray-200 [&:not(:first-child)]:dark:border-gray-700 [&:not(:first-child)]:pt-6',
      className
    )}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';
CardFooter.propTypes = {
  className: PropTypes.string,
};

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}
